import React, { useEffect, useRef, useState } from 'react';
import { getShellActions, loadApp, AuthToken, DafAuthConfig, DafGrantType } from '@nab/x-runtime';
import { Modal } from '@nab/nui-react';
import constants from '../../util/constants';
import { StyledModalContent } from './IdentityApp.style';
import { errorMessage } from 'util/unknown-error';
import merge from 'lodash.merge';
import { generateUniqueId } from '../../util/generateUniqueId';
import NabDafInMemoryCacheStrategy from '../../runtime/auth-token-cache-strategy/nab-daf-in-memory';

const IdentityApp: React.FunctionComponent<{ config }> = ({ config }) => {
	const appActions = getShellActions();
	const identityRootReference = useRef(null);
	const modelReference = useRef(null);
	const modalContentRef = useRef<HTMLDivElement>(null);
	const [showModal, setShowModal] = useState(false);
	const [identityAppConfig, setIdentityAppConfig] = useState(config);

	const miniappLoader = document.querySelector('mini-app-loader');

	const genIdentityAppTag = (tag: string) => {
		return {
			appMetaData: {
				id: tag,
				tag: tag,
				entryPoint: tag + '.js',
			},
		};
	};

	const handleLoginRequest = (event) => {
		setIdentityAppConfig(
			merge(
				identityAppConfig,
				{ appConfig: event?.appConfig || {} },
				genIdentityAppTag(event?.identityAppTag || event?.tag || identityAppConfig.appMetaData.tag),
			),
		);
		setShowModal(true);
	};

	const handleUserAuthenticatedEvent = async (data) => {
		if (data?.ssc) {
			const { user } = window.digitalData;
			window.digitalData.user = { ...user, ssc: data.ssc };
		}

		// Manually add token to in-memory cache
		if (data?.bearerToken) {
			const cacheStrategy = NabDafInMemoryCacheStrategy.getInstance();

			const token: AuthToken<DafAuthConfig> = {
				id: generateUniqueId(),
				tokenValue: data.bearerToken,
				expires: data.expires ? Date.now() + data.expires * 1000 : Date.now() + 15 * 60 * 1000,
				config: {
					isSeed: true,
					tokenSource: 'DAF',
					tokenType: 'Bearer',
					grantType: DafGrantType.UsernamePassword,
					context: identityAppConfig?.appConfig?.context || 'external',
					environment: identityAppConfig?.appConfig?.environment,
					flags: null,
				},
				parentId: undefined,
			};

			await cacheStrategy.addToken(token);

			// Check to see if 'nab-idp-usernamepassword' is displayed in the DOM. If it is, then update the auth config to the 'nab:usernamePassword' grant type. If another variant of the identity-miniapp is used, don't update grant type as this leads to triggering an incorrect authentication strategy.
			// Passing 'false' as the second parameter to updateApps prevents a re-render of the mini-apps by updating the config internally within the SDK rather than propagating it directly the mini-apps.
			const isUsernamePasswordFlow = document.querySelector(constants.IDMA_USERNAMEPASSWORD_TAG) !== null;

			if (isUsernamePasswordFlow) {
				appActions.updateApps(
					{
						authConfig: {
							grantType: DafGrantType.UsernamePassword,
						} as DafAuthConfig,
					},
					false,
				);
			}
		}

		const authenticatedEvent = new Event(constants.EVENT_USER_AUTHENTICATED_XV2);
		miniappLoader && miniappLoader.dispatchEvent(authenticatedEvent);

		if (identityAppConfig?.properties?.skipAuthOnRender) {
			// TODO: find context on this, doesn't exist on OSGI configs
			await appActions.authenticate();
		}
		setShowModal(false);
	};

	const handleUserCancelEvent = () => {
		setShowModal(false);
	};

	const handleESCKeyEvent = (event) => {
		if (event.key === 'Escape') {
			setShowModal(false);
		}
	};

	const displayMessageHandler = (event) => {
		appActions.dispatchEvent(constants.EVENT_CLEAR_USER_DATA);
		appActions.dispatchEvent(constants.EVENT_DISPLAY_MESSAGE, event.detail);
	};

	useEffect(() => {
		//Listening for login request
		appActions.addEventListener(constants.EVENT_LOGIN, handleLoginRequest);

		//Listening for user authenticated event
		appActions.addEventListener(constants.EVENT_USER_AUTHENTICATED_XV2, handleUserAuthenticatedEvent);

		//Listening for user cancel event
		appActions.addEventListener(constants.EVENT_USER_CANCELLED_XV2, handleUserCancelEvent);

		miniappLoader.addEventListener(constants.EVENT_DISPLAY_MESSAGE, displayMessageHandler);

		//Listening for ESC Key event
		document.addEventListener(constants.KEY_DOWN, handleESCKeyEvent, false);

		return () => {
			//Unsubscribe for login request
			appActions.removeEventListener(constants.EVENT_LOGIN, handleLoginRequest);

			//Unsubscribe for user authenticated event
			appActions.removeEventListener(constants.EVENT_USER_AUTHENTICATED_XV2, handleUserAuthenticatedEvent);

			//Unsubscribe for user cancel event
			appActions.removeEventListener(constants.EVENT_USER_CANCELLED_XV2, handleUserCancelEvent);

			miniappLoader.removeEventListener(constants.EVENT_DISPLAY_MESSAGE, displayMessageHandler);
		};
	}, []);

	useEffect(() => {
		let loaderPromise;

		if (showModal) {
			let loadAppConfig = {
				...identityAppConfig,
				...{ hostElement: identityRootReference.current },
			};

			const load = async () => {
				try {
					loaderPromise = loadApp(loadAppConfig);
					await loaderPromise;

					await appActions.dispatchEvent(constants.APP_SHELL_LOADED_EVENT, {
						appLoaded: true,
						appName: identityAppConfig.appMetaData.tag,
					});
				} catch (e) {
					setShowModal(false);
					await appActions.dispatchEvent(constants.APP_SHELL_LOADED_EVENT, {
						appLoaded: false,
						appName: identityAppConfig.appMetaData.tag,
						errorMessage: errorMessage(e),
					});
				}
			};
			load();
		}
		return () => {
			if (loaderPromise) {
				loaderPromise.then((app) => {
					app.unload();
				});
			}
		};
	}, [identityRootReference, showModal]);

	const shadowRoot = document.querySelector(identityAppConfig.appMetaData.tag)?.shadowRoot;

	// Custom backdrop click handler to properly detect clicks inside shadow DOM and dismiss the modal
	useEffect(() => {
		if (!showModal) return;

		const handleDocumentClick = (e: MouseEvent) => {
			const modalElement = document.getElementById('shell-im');
			if (!modalElement) return;

			const target = e.target as HTMLElement;

			/* 
				Check if click is inside the modal content
				1st Check: Is the click inside the modal content div?
				2nd Check: Is the clicked element or any of its ancestors the modal?
				3rd Check: If the clicked element is inside a Shadow DOM, is the shadow root's host element inside the modal? 
			*/
			const isInsideModalContent =
				modalContentRef.current?.contains(target) ||
				target.closest('#shell-im') !== null ||
				(target.getRootNode && (target.getRootNode() as any)?.host?.closest('#shell-im'));

			// Only close if clicking outside the modal content
			if (!isInsideModalContent) {
				setShowModal(false);
			}
		};

		// Small timeout to ensure the modal is rendered before attaching the handler
		const timeout = setTimeout(() => {
			document.addEventListener('click', handleDocumentClick, true);
		}, 100);

		return () => {
			clearTimeout(timeout);
			document.removeEventListener('click', handleDocumentClick, true);
		};
	}, [showModal]);

	return (
		<div ref={modelReference}>
			{showModal && (
				<Modal
					id="shell-im"
					title=""
					rootNode={shadowRoot}
					hasClose={identityAppConfig?.appConfig?.hasCloseButton ?? true}
					hasBackdropClose={false}
					onClose={handleUserCancelEvent}
				>
					<StyledModalContent ref={modalContentRef}>
						<div ref={identityRootReference} />
					</StyledModalContent>
				</Modal>
			)}
		</div>
	);
};

export default IdentityApp;
